import styles from "./early-payoff.module.css"
import React from "react"
import merge from "lodash/merge"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import Line from "story/components/graphs/line"
import * as graphUtil from "shared/graphs/util"
import * as loan from "shared/loan-payoff"
import AccessibilityTable from "activities/components/shared/accessibility-table"

import assign from "object-assign"

const graphData = (months, balance, rate, payment, added) => {
  let interest = 0
  const data = Array.from(Array(months + 12)).reduce((memo, x, idx) => {
    if (idx === 0) memo.push([idx, balance, balance])
    else {
      const last = memo[memo.length - 1]
      const baselineBalance = last[1]
      const addedBalance = last[2]
      const paymentForAdded = loan.getPrincipalPayment(addedBalance, payment, rate)
      interest += payment - paymentForAdded
      memo.push([idx, loan.minusPayment(baselineBalance, loan.getPrincipalPayment(baselineBalance, payment, rate)), loan.minusPayment(addedBalance, paymentForAdded + added)])
    }
    return memo
  }, [])

  return {data, interest}
}

const LoanPayoff_ = shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const {price, payment, term, rate, additionalPayment, resetKey} = getState()
    const {theme, amountTitle, downPayment, loanTerm, interestRate, addedPayment, id, ariaLabel} = getProps()

    const priceMinusDown = price - payment
    const decimalRate = rate / 100
    const monthlyPayment = loan.calculatePayment(priceMinusDown, decimalRate, term * 12)
    const {data, interest} = graphData(term * 12, priceMinusDown, decimalRate, monthlyPayment, additionalPayment)

    const payoffDateObj = loan.getPayoffDate(loan.getMonthsToPayoff(data))
    const baselineInterestPaid = monthlyPayment * term * 12 - priceMinusDown
    const interestSavings = baselineInterestPaid - interest

    const isDisabled = priceMinusDown < 0

    const {max: __addedMax, min: addedMin, step: addedStep} = addedPayment
    const _addedMax = typeof __addedMax === "function" ? __addedMax(monthlyPayment) : __addedMax

    const addedMax = _addedMax || 2 * addedStep

    const accessibilityTableHeadings = [
      {name: "Months", key: "month"},
      {name: "Baseline", key: "baseline"},
      {name: "Early", key: "early"}
    ]

    const accessibilityTableData = loan.getYears(data).map((row) => ({
      month: row[0],
      baseline: util.formatDollarsNoCents(row[1]),
      early: util.formatDollarsNoCents(row[2])
    }))

    const accessibilityTableLabel = "Loan Payoff Table"

    return (
      <div>
        <shared.SummaryCalcContainer>
          <shared.InputsContainer>
            <inputs.CalcInput id={id} title={amountTitle || "Loan Amount"} onChange={updateState("price", util.fromString)} initialValue={price} resetKey={resetKey} />
            {downPayment.show && (
              <inputs.RangeInput
                id={id}
                title="Down Payment"
                onChange={updateState("payment", util.fromString)}
                initialValue={payment}
                secondaryValue={payment / price}
                min={downPayment.min}
                max={price * 0.8}
                step={downPayment.step}
                units="$"
                disabled={isDisabled}
                resetKey={resetKey}
                color={theme["color-1"]}
              />
            )}
            <inputs.RangeInput
              id={id}
              title="Loan Term"
              onChange={updateState("term", util.fromString)}
              initialValue={term}
              min={loanTerm.min}
              max={loanTerm.max}
              step={loanTerm.step}
              valueType="years-full"
              units="years"
              disabled={isDisabled}
              resetKey={resetKey}
              color={theme["color-1"]}
            />
            <inputs.RangeInput
              id={id}
              title="Interest Rate"
              onChange={updateState("rate", parseFloat)}
              initialValue={rate}
              min={interestRate.min}
              max={interestRate.max}
              step={interestRate.step}
              valueType="percent"
              units="%"
              disabled={isDisabled}
              resetKey={resetKey}
              color={theme["color-1"]}
            />
            <inputs.RangeInput
              id={id}
              title="Added Monthly Amt"
              onChange={updateState("additionalPayment", util.fromString)}
              initialValue={additionalPayment}
              min={addedMin}
              max={addedMax}
              step={addedStep}
              units="$"
              disabled={isDisabled}
              resetKey={resetKey}
              color={theme["color-1"]}
            />
          </shared.InputsContainer>
          <shared.ChartContainer className={styles["chart"]}>
            <Line
              ariaLabel={ariaLabel}
              yAxis="Loan Amount"
              values={loan.getYears(data)}
              colors={["#5c5761", graphUtil.getColor(theme, 1)]}
              type="calculator"
              tooltip={["Year", {name: "Baseline", type: "currency", color: "#5c5761"}, {name: "Early", type: "currency", color: graphUtil.getColor(theme, 1)}]}
            />
          </shared.ChartContainer>
          <shared.SummaryContainer className={styles["summary"]}>
            <callouts.Callout>
              <callouts.Detail
                label="Monthly Payment"
                dataTestId="monthly-payment"
                result={util.formatDollarsNoCents(util.ifNegative(Number(monthlyPayment) + Number(additionalPayment)))}
              />
              <callouts.Detail label="Interest Savings" dataTestId="interest-savings" result={util.formatDollarsNoCents(util.ifNegative(interestSavings))} />
              <callouts.Detail label="Interest to be Paid" dataTestId="interest-paid" result={util.formatDollarsNoCents(util.ifNegative(interest))} />
              <callouts.Detail label="Payoff Date" dataTestId="payoff" result={payoffDateObj ? loan.getReadableDate(payoffDateObj) : "N/A"} />
            </callouts.Callout>
          </shared.SummaryContainer>
        </shared.SummaryCalcContainer>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {price, payment, term, rate, additionalPayment} = state
      const decimalRate = rate / 100
      const monthlyPayment = loan.calculatePayment(price, decimalRate, term * 12)
      const {data, interest} = graphData(term * 12, price, decimalRate, monthlyPayment, additionalPayment)
      const payoffDateObj = loan.getPayoffDate(loan.getMonthsToPayoff(data))
      const priceMinusDown = price - payment
      return assign(state, {
        balance: price,
        monthlyPayment: util.round(monthlyPayment) + util.round(additionalPayment),
        interestSavings: util.round(interest),
        interestPaid: util.round(monthlyPayment * term * 12 - priceMinusDown),
        payoffDate: payoffDateObj ? loan.getReadableDate(payoffDateObj) : "N/A"
      })
    },
    defaultProps: {
      id: "loan-payoff",
      className: "early-payoff",
      title: "Loan Payoff Calculator",
      subtitle: "Explore what it would take to pay off your loan early.",
      downPayment: {
        show: false,
        step: 1000
      },
      loanTerm: {
        min: 1,
        max: 10,
        step: 1
      },
      interestRate: {
        min: 1.5,
        max: 8.0,
        step: 0.05
      },
      addedPayment: {
        min: 0,
        max: 100,
        step: 10
      }
    },
    defaultState: {
      price: 1000,
      payment: 0,
      term: 5,
      rate: 4.75,
      additionalPayment: 20
    }
  }
)

export default (props) => (
  <LoanPayoff_
    {...merge(
      {
        id: "debt-management",
        title: "Debt Payoff Calculator",
        subtitle: "Explore what it would take to pay off your personal loan early.",
        amountTitle: "Loan balance",
        loanTerm: {
          min: 1,
          max: 30,
          step: 1
        },
        addedPayment: {
          min: 0,
          max: (payment) => Math.floor((payment * 5) / 100) * 100,
          step: 50
        },
        initialState: {
          price: 10000,
          term: 10,
          rate: 6,
          additionalPayment: 50
        },
        ariaLabel:
          "This line graph visually represents how much sooner you could pay off your loan. Read below to find the monthly payment, interest saved, interest to be paid, and the date the loan will be paid off."
      },
      props
    )}
  />
)
